//Built-in libraries

//Third party libraries

import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
//Services
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
import { useFileUpload } from '@/hooks/useFileUpload';
import { notifyUser } from '@/store/Toast';

//STP Libraries

//Assets and Miscellaneous

//Component Begin

export default function Documents() {
  const { setCurrentStep } = useApplicationFormContext();
  const {
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext();
  const params = useParams();
  const dispatch = useDispatch();
  const { upload } = useFileUpload({ signAsset: signApplicationPublicAsset });
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const [uploadErrors, setUploadErrors] = useState<{
    proofOfOwnership?: string;
    proofOfOccupancy?: string;
    zeroIncomeAffidavit?: string;
    bankStatements?: string;
    proofOfIncome?: string;
  }>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isProcessingBankStatements, setIsProcessingBankStatements] = useState(false);

  // Watch for file changes
  const proofOfOwnership = watch('documents.proofOfOwnership');
  const proofOfOccupancy = watch('documents.proofOfOccupancy');
  const zeroIncomeAffidavit = watch('documents.zeroIncomeAffidavit');
  const bankStatements = watch('documents.bankStatements');
  const proofOfIncome = watch('documents.proofOfIncome');

  // Debug: Log errors to see what's happening
  useEffect(() => {
    console.log('Form errors:', errors);
    console.log('Documents errors:', errors.documents);
  }, [errors]);

  // Helper to upload a file and set meta
  const uploadFileAndSetMeta = useCallback(
    async (fieldName: string, file: File) => {
      setUploadingField(fieldName);
      setUploadErrors((prev) => ({ ...prev, [fieldName]: undefined }));
      try {
        const signAssetPayload = [
          {
            formId: String(params.applicationFormId),
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            field: fieldName,
          },
        ];
        const { sign, files } = await upload({
          signAssetPayload,
          files: [file],
        });
        // Set form value to meta (do not call addApplicationFile)
        if (fieldName === 'bankStatements') {
          // For bank statements, append to existing array
          const currentValue = watch(`documents.${fieldName}`) || [];
          // Filter out any File objects to avoid re-processing
          const existingFiles = currentValue.filter((item: any) => !(item instanceof File));
          setValue(
            `documents.${fieldName}`,
            [
              ...existingFiles,
              {
                name: file.name,
                path: sign.path,
                key: sign.key,
                type: file.type,
                field: fieldName,
                description: fieldName,
              },
            ],
            { shouldValidate: true }
          );
        } else {
          // For other fields, replace the array
          setValue(
            `documents.${fieldName}`,
            [
              {
                name: file.name,
                path: sign.path,
                key: sign.key,
                type: file.type,
                field: fieldName,
                description: fieldName,
              },
            ],
            { shouldValidate: true }
          );
        }
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${file.name} uploaded successfully`,
            toastType: 'success',
          })
        );
      } catch (_err: any) {
        const errorMessage = `Failed to upload ${file.name}`;
        setUploadErrors((prev) => ({ ...prev, [fieldName]: errorMessage }));
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: errorMessage,
            toastType: 'failed',
          })
        );
      } finally {
        setUploadingField(null);
      }
    },
    [dispatch, params.applicationFormId, setValue, upload, watch]
  );

  // Effect for proofOfOwnership
  useEffect(() => {
    if (proofOfOwnership && Array.isArray(proofOfOwnership) && proofOfOwnership[0] instanceof File) {
      uploadFileAndSetMeta('proofOfOwnership', proofOfOwnership[0]);
    }
  }, [proofOfOwnership, uploadFileAndSetMeta]);

  // Effect for proofOfOccupancy
  useEffect(() => {
    if (proofOfOccupancy && Array.isArray(proofOfOccupancy) && proofOfOccupancy[0] instanceof File) {
      uploadFileAndSetMeta('proofOfOccupancy', proofOfOccupancy[0]);
    }
  }, [proofOfOccupancy, uploadFileAndSetMeta]);

  // Effect for zeroIncomeAffidavit
  useEffect(() => {
    if (zeroIncomeAffidavit && Array.isArray(zeroIncomeAffidavit) && zeroIncomeAffidavit[0] instanceof File) {
      uploadFileAndSetMeta('zeroIncomeAffidavit', zeroIncomeAffidavit[0]);
    }
  }, [zeroIncomeAffidavit, uploadFileAndSetMeta]);

  // Effect for bankStatements (multiple files)
  useEffect(() => {
    if (
      bankStatements &&
      Array.isArray(bankStatements) &&
      bankStatements.length > 0 &&
      bankStatements.some((file) => file instanceof File) &&
      !isProcessingBankStatements
    ) {
      setIsProcessingBankStatements(true);

      // Handle multiple files for bank statements
      const filesToUpload = bankStatements.filter((file) => file instanceof File);

      // Process files sequentially to avoid conflicts
      const processFiles = async () => {
        for (let i = 0; i < filesToUpload.length; i++) {
          const file = filesToUpload[i];
          await uploadFileAndSetMeta('bankStatements', file);
        }
        setIsProcessingBankStatements(false);
      };

      processFiles();
    }
  }, [bankStatements, uploadFileAndSetMeta, isProcessingBankStatements]);

  // Effect for proofOfIncome
  useEffect(() => {
    if (proofOfIncome && Array.isArray(proofOfIncome) && proofOfIncome[0] instanceof File) {
      uploadFileAndSetMeta('proofOfIncome', proofOfIncome[0]);
    }
  }, [proofOfIncome, uploadFileAndSetMeta]);

  // Helper function to get validation error message
  const getValidationError = (fieldName: string) => {
    if (errors.documents && showValidationErrors) {
      const documentsErrors = errors.documents as any;
      const error = documentsErrors[fieldName]?.message;
      console.log(`Form validation error for ${fieldName}:`, error);
      return error;
    }
    return null;
  };

  // Helper function to get the single error message for a field (prioritizes upload errors)
  const _getSingleErrorMessage = (fieldName: string) => {
    // Priority 1: Upload errors
    const uploadError = uploadErrors[fieldName as keyof typeof uploadErrors];
    if (uploadError) {
      return uploadError;
    }

    // Priority 2: Form validation errors
    const validationError = getValidationError(fieldName);
    if (validationError) {
      return validationError;
    }

    // No errors
    return null;
  };

  return (
    <div className='sm:rounded-r-md sm:overflow-hidden'>
      <div className='py-6 px-4 space-y-6 sm:p-6'>
        <h3 className='text-lg leading-6 font-medium text-green-500'>Documents (Required)</h3>
        <div className='grid grid-cols-1 gap-6'>
          <div className='col-span-1'>
            <HookFileInput
              name='documents.proofOfOwnership'
              label='Proof of Ownership - Title Deed'
              id='proofOfOwnership'
              multiple={false}
              disabled={uploadingField === 'proofOfOwnership'}
            />
          </div>
          <div className='col-span-1'>
            <HookFileInput
              name='documents.proofOfOccupancy'
              label='Proof of Occupancy - Utility Bill'
              id='proofOfOccupancy'
              multiple={false}
              disabled={uploadingField === 'proofOfOccupancy'}
            />
          </div>
          <div className='col-span-1'>
            <HookFileInput
              name='documents.zeroIncomeAffidavit'
              label='Zero Income Affidavit (Optional)'
              id='zeroIncomeAffidavit'
              multiple={false}
              disabled={uploadingField === 'zeroIncomeAffidavit'}
            />
          </div>
          <div className='col-span-1'>
            <HookFileInput
              name='documents.bankStatements'
              label='Bank Statements (Multiple files allowed)'
              id='bankStatements'
              multiple={true}
              disabled={uploadingField === 'bankStatements'}
            />
          </div>
          <div className='col-span-1'>
            <HookFileInput
              name='documents.proofOfIncome'
              label='Proof of Income (Optional)'
              id='proofOfIncome'
              multiple={false}
              disabled={uploadingField === 'proofOfIncome'}
            />
          </div>
        </div>
        <div className='flex gap-4 justify-end mt-5'>
          <Button label='Previous' btnStyle='neutral' type='button' onClick={() => setCurrentStep((prev) => prev - 1)} />
          <Button
            label='Next'
            btnStyle='primary'
            type='submit'
            onClick={async () => {
              // Only show validation errors when user tries to proceed
              setShowValidationErrors(true);
              await trigger('documents');
            }}
          />
        </div>
      </div>
    </div>
  );
}
